import {
  ChangeEvent,
  Dispatch,
  MouseEvent,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import queryHooks from '../../../common/queryHooks/index.ts';
import Paginator from '../../../components/layout/paginator.tsx';
import { useDebounceValue } from '../../../common/useDebounce.ts';
import { MedAPI } from '../../../common/api/med.ts';

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  customerLocationId: number;
  setOutboundMeds: Dispatch<
    SetStateAction<MedAPI.Response.ReadOneBySerialNumberPedigree[]>
  >;
}

const MedSearchModal = ({
  isOpen,
  setIsOpen,
  customerLocationId,
  setOutboundMeds,
}: IProps) => {
  const modal = useRef<HTMLDivElement>(null);

  const initValues = () => ({
    serialNumber: '',
    ndc: '',
    medName: '',
    manufacturerName: '',
    lotNumber: '',
  });

  const [values, setValues] = useState(initValues());
  const [pageNumber, setPageNumber] = useState(1);

  const debouncedValues = useDebounceValue(values, 500);

  const {
    data: medList,
    isLoading,
    refetch,
  } = queryHooks.med.useReadManyBySearch(
    {
      values: {
        ...debouncedValues,
        customerLocationId,
      },
      paginationOptions: {
        pageNumber,
        pageSize: 10,
      },
    },
    {
      enabled: Object.values(debouncedValues).some(val => Boolean(val)),
    }
  );

  useEffect(() => {
    const { current } = modal;
    if (current) M.Modal.init(current, { onCloseEnd: () => setIsOpen(false) });

    return () => {
      if (current) M.Modal.getInstance(current)?.destroy();
    };
  }, []);

  const getModal = () =>
    modal.current ? M.Modal.getInstance(modal.current) : null;

  useLayoutEffect(() => {
    const modal = getModal();
    setValues(initValues());
    if (modal) {
      if (isOpen) {
        modal.open();
      } else {
        modal.close();
      }
    }
  }, [isOpen]);

  const onChange = ({ target: { id, value } }: ChangeEvent<HTMLInputElement>) =>
    setValues(prev => ({ ...prev, [id]: value }));

  const cancel = (event?: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    getModal()?.close();
  };

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">Search Meds</h5>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s12 m6 l4">
          <input
            id="serialNumber"
            type="text"
            value={values.serialNumber}
            onChange={onChange}
          />
          <label htmlFor="serialNumber">Serial #</label>
        </div>
        <div className="input-field col s12 m6 l4">
          <input id="ndc" type="text" value={values.ndc} onChange={onChange} />
          <label htmlFor="ndc">NDC</label>
        </div>
        <div className="input-field col s12 m6 l4">
          <input
            id="medName"
            type="text"
            value={values.medName}
            onChange={onChange}
          />
          <label htmlFor="medName">Med Name</label>
        </div>
      </div>
      <div className="row">
        <div className="input-field col s12 m6 l4">
          <input
            id="manufacturerName"
            type="text"
            value={values.manufacturerName}
            onChange={onChange}
          />
          <label htmlFor="manufacturerName">Manufacturer</label>
        </div>
        <div className="input-field col s12 m6 l4">
          <input
            id="lotNumber"
            type="text"
            value={values.lotNumber}
            onChange={onChange}
          />
          <label htmlFor="lotNumber">Lot #</label>
        </div>
        <div className="input-field col s12 m6 l4">
          <a
            href="/"
            className="btn-small blue white-text waves-effect waves-light col s12"
            onClick={ev => {
              ev.preventDefault();
              refetch();
            }}
          >
            Search Meds
          </a>
        </div>
      </div>
      <div className="row">
        <table className="highlight" style={{ minHeight: '120px' }}>
          <thead>
            <tr>
              <th>Serial #</th>
              <th>NDC</th>
              <th>Med Name</th>
              <th>Manufacturer</th>
              <th>Lot #</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td style={{ padding: '4px' }} colSpan={6}>
                  <div className="progress">
                    <div className="indeterminate" />
                  </div>
                </td>
              </tr>
            )}
            {medList?.data?.map(med => {
              const {
                med: { id, lotNumber, serialNumber },
                redbook: { ndc },
                redbookDetail: { medName, manufacturerName },
              } = med;
              return (
                <tr key={id}>
                  <td>{serialNumber}</td>
                  <td>{ndc}</td>
                  <td>{medName}</td>
                  <td>{manufacturerName}</td>
                  <td>{lotNumber}</td>
                  <td>
                    <a
                      href="/"
                      onClick={ev => {
                        ev.preventDefault();
                        setOutboundMeds(prev => [...prev, med]);
                      }}
                    >
                      Add Med
                    </a>
                  </td>
                </tr>
              );
            })}
            {medList && !medList.data.length && (
              <tr>
                <td>No Results</td>
              </tr>
            )}
          </tbody>
        </table>
        <Paginator
          pageSize={10}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          totalItems={medList?.total ?? 0}
        />
      </div>
      <div className="row">
        <div className="col s12 m4 offset-m4">
          <a
            href="/"
            className="btn-small blue white-text waves-effect waves-light col s12"
            onClick={cancel}
          >
            close
          </a>
        </div>
      </div>
    </div>
  );
};

export default MedSearchModal;
